import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle, Phone, Mail, Facebook, Instagram, ArrowUpRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { IMAGES, waLink, PHONE_DISPLAY, EMAIL } from "../lib/site";

const NAV_ROUTES = [
  { key: "start", to: "/" },
  { key: "about", to: "/ueber-mich" },
  { key: "topics", to: "/themen" },
  { key: "methods", to: "/arbeitsweise" },
  { key: "pricing", to: "/preise" },
  { key: "faq", to: "/faq" },
  { key: "feedback", to: "/feedback" },
  { key: "contact", to: "/kontakt" },
];

export const LanguageSwitcher = () => {
  const { lang, setLang } = useLanguage();
  return (
    <div
      className="flex items-center rounded-full border border-warmline bg-white p-1"
      role="group"
      aria-label="Sprache wählen / Dil seçin / Choose language"
      data-testid="language-switcher"
    >
      {["de", "tr", "en"].map((code) => (
        <button
          key={code}
          data-testid={`lang-switch-${code}`}
          onClick={() => setLang(code)}
          aria-pressed={lang === code}
          className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors ${
            lang === code ? "bg-petrol text-white" : "text-slate2 hover:text-petrol"
          }`}
        >
          {code}
        </button>
      ))}
    </div>
  );
};

export const WhatsAppButton = ({ label, className, testId = "whatsapp-cta" }) => {
  const { t } = useLanguage();
  return (
    <a
      href={waLink(t.wa.message)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t.common.whatsappAria}
      data-testid={testId}
      className={`group inline-flex items-center gap-1.5 rounded-full bg-teal px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-teal-dark ${className || ""}`}
    >
      <MessageCircle className="h-3.5 w-3.5 transition-transform group-hover:scale-110" aria-hidden="true" />
      <span>{label || t.common.whatsappCta}</span>
    </a>
  );
};

export const Header = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <header className="sticky top-0 z-50 border-b border-warmline bg-white/95 backdrop-blur-md" data-testid="site-header">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-4 px-4 sm:px-8">
        <Link to="/" className="flex min-w-0 items-center gap-3" data-testid="header-logo-link" aria-label="Startseite">
          <img src={IMAGES.logo} alt="Logo Praxis Naciye Rafiq-Arli" className="h-14 w-14 rounded-full" />
          <span className="hidden min-w-0 flex-col leading-tight md:flex">
            <span className="truncate font-display text-base font-semibold tracking-tight text-petrol">Naciye Rafiq-Arli</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-teal-dark">Praxis für Hypnose & Psychotherapie</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex" aria-label="Hauptnavigation" data-testid="desktop-nav">
          {NAV_ROUTES.map(({ key, to }) => (
            <NavLink
              key={key}
              to={to}
              data-testid={`nav-link-${key}`}
              className={({ isActive }) =>
                `relative py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-teal-dark after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-full after:bg-teal"
                    : "text-ink hover:text-teal-dark"
                }`
              }
            >
              {t.nav[key]}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <LanguageSwitcher />
          <span className="hidden xl:inline-flex">
            <WhatsAppButton testId="whatsapp-cta-header" className="px-5 py-2.5" />
          </span>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label={open ? t.common.close : t.common.menu}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-warmline bg-white text-petrol lg:hidden"
          >
            {open ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-warmline bg-white lg:hidden"
            aria-label="Mobile Navigation"
            data-testid="mobile-nav"
          >
            <div className="px-4 py-4">
              {NAV_ROUTES.map(({ key, to }) => (
                <NavLink
                  key={key}
                  to={to}
                  data-testid={`mobile-nav-link-${key}`}
                  className={({ isActive }) =>
                    `flex items-center justify-between border-b border-warmline py-4 font-display text-lg font-semibold transition-colors ${
                      isActive ? "text-teal-dark" : "text-petrol"
                    }`
                  }
                >
                  {t.nav[key]}
                  <ArrowUpRight className="h-4 w-4 text-slate2" aria-hidden="true" />
                </NavLink>
              ))}
              <div className="pt-4">
                <WhatsAppButton label={t.common.whatsappCtaLong} testId="whatsapp-cta-mobile" className="w-full justify-center py-3 text-sm" />
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export const FloatingWhatsApp = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 480);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <motion.a
      href={waLink(t.wa.message)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t.common.whatsappAria}
      data-testid="floating-whatsapp-button"
      initial={false}
      animate={{ opacity: visible ? 1 : 0, scale: visible ? 1 : 0.6, pointerEvents: visible ? "auto" : "none" }}
      transition={{ duration: 0.25 }}
      className="fixed bottom-4 right-4 z-50 flex items-center gap-1.5 rounded-full bg-teal px-3.5 py-2 text-xs font-bold text-white shadow-lg shadow-petrol/20 hover:bg-teal-dark"
    >
      <MessageCircle className="h-4 w-4" aria-hidden="true" />
      <span className="hidden sm:inline">{t.common.whatsappCta}</span>
    </motion.a>
  );
};

export const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer className="border-t border-warmline bg-white" data-testid="site-footer">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-x-8 gap-y-4 px-5 py-6 sm:px-8">
        <Link to="/" className="flex items-center gap-2.5" data-testid="footer-logo-link" aria-label="Startseite">
          <img src={IMAGES.logo} alt="" className="h-10 w-10 rounded-full" />
          <span className="leading-tight">
            <span className="block font-display text-sm font-semibold text-petrol">Naciye Rafiq-Arli</span>
            <span className="block text-[9px] font-bold uppercase tracking-[0.14em] text-teal-dark">Hypnose & Psychotherapie</span>
          </span>
        </Link>
        <nav aria-label={t.footer.navTitle} className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
          {NAV_ROUTES.map(({ key, to }) => (
            <Link key={key} to={to} data-testid={`footer-nav-${key}`} className="text-slate2 transition-colors hover:text-teal-dark">
              {t.nav[key]}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-2.5">
          <a
            href={`tel:${PHONE_DISPLAY.replace(/\s/g, "")}`}
            aria-label={PHONE_DISPLAY}
            data-testid="footer-phone"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-light text-teal-dark transition-colors hover:bg-teal hover:text-white"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
          </a>
          <a
            href={`mailto:${EMAIL}`}
            aria-label={EMAIL}
            data-testid="footer-email"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-light text-teal-dark transition-colors hover:bg-teal hover:text-white"
          >
            <Mail className="h-4 w-4" aria-hidden="true" />
          </a>
          <a
            href="https://www.facebook.com/HypnosePraxisHamburg"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            data-testid="footer-social-facebook"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-light text-teal-dark transition-colors hover:bg-teal hover:text-white"
          >
            <Facebook className="h-4 w-4" aria-hidden="true" />
          </a>
          <a
            href="https://www.instagram.com/hypnosepraxishamburg"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            data-testid="footer-social-instagram"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-light text-teal-dark transition-colors hover:bg-teal hover:text-white"
          >
            <Instagram className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </div>
      <div className="bg-petrol px-5 py-3">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-5 gap-y-1 text-xs text-white/70 sm:justify-between">
          <span>{t.footer.copyright}</span>
          <span className="flex gap-4">
            <Link to="/datenschutz" data-testid="footer-privacy-link" className="underline-offset-4 hover:text-white hover:underline">
              {t.footer.privacy}
            </Link>
            <Link to="/impressum" data-testid="footer-imprint-link" className="underline-offset-4 hover:text-white hover:underline">
              {t.footer.imprint}
            </Link>
          </span>
        </div>
      </div>
    </footer>
  );
};
